// #kinship-quiz-unification — pure puzzle assembly logic.
//
// Inputs: a level, an ego-aware label-lookup Map (pre-fetched via
// the kinshipLabels endpoint for BOTH ego genders), and a per-
// session RNG. Outputs: a QuizPuzzle ready for rendering — chain
// + characters + 4 answer options (1 correct + 3 distractors,
// shuffled).
//
// No engine calls here. The component fetches all chains it
// might need at mount and passes the merged ego-aware Map in.
//
// Distractor strategies, per level:
//   L1: pick 3 other direct-relation chains from the L1 pool.
//   L2: pick 3 other L2 chains from the filtered pool. Dispatch
//       prescribed "mutate one atom" — we use pool-other-chain
//       since L2 chains share the same gen+1/+2 distribution
//       and the simpler strategy produces well-mixed near-misses.
//   L3: 3 explicit mutations — flipped-ego + descent-flip
//       (Son↔Daughter) + sibling-flip (Brother↔Sister). Falls
//       back to pool-other-chain when a mutation collides.
//   L4: 3 mutations of the chain's atoms in order. Falls back
//       to pool-other-chain on collision.

import {
  pickName, atomForcedGender, validateNameGenderConsistency,
  MALE_NAMES, FEMALE_NAMES,
  type CharacterGender, type PuzzleCharacter, type QuizAtom,
} from './quizNames'

const MALE_NAMES_REF: readonly string[] = MALE_NAMES
const FEMALE_NAMES_REF: readonly string[] = FEMALE_NAMES
import {
  chainKey, isSelfTerm, egoKey,
  type Chain, type EgoLookupKey,
} from './quizChainPool'
import type { PoolEntry } from '../api/hooks/useChainPool'

export interface KinshipLabel {
  term: string
  isAgeDependent: boolean
  options?: string[]
}

export type EgoLookup = ReadonlyMap<EgoLookupKey, KinshipLabel>

export interface AnswerOption {
  chainKey: string      // backing chain key — useful for diagnostics
  term: string          // displayed Telugu term (possibly slash form)
  isCorrect: boolean
  /** Optional label of WHY this option exists. Used for analytics
   *  + L3 teaching cues; not displayed. The 'adjacent-level'
   *  source identifies distractors pulled from a neighbouring
   *  level's pool — see #kinship-quiz-distractor-fallback. */
  source?: 'correct' | 'pool' | 'flipped-ego' | 'mutation' | 'adjacent-level'
}

// #kinship-quiz-level-6-named-terms — visualSteps overrides the
// default per-atom chain-arrow rendering. For an L6 puzzle like
// [Mama, Daughter] male ego with chain [Mother, Brother, Daughter]:
//
//   characters = [MOTHER-char, BROTHER-char, DAUGHTER-char]
//   visualSteps = [
//     { label: 'మామయ్య',  characterIndex: 1 },  // collapses 2 atoms
//     { label: 'కూతురు',  characterIndex: 2 },
//   ]
//
// ChainVisualization renders one arrow per visualStep when present,
// landing on characters[characterIndex]. Intermediate characters
// (the MOTHER) are skipped from the visualisation but still
// participate in the engine's label resolution.
export interface VisualStep {
  /** Telugu label shown on the arrow. */
  label: string
  /** Index into puzzle.characters for the arrow's destination. */
  characterIndex: number
}

// #quiz-l7-ui — per-puzzle metadata for L7 inverse-relations.
// L7 puzzles invert the visual perspective: the yellow ego is B
// (chain TARGET, the inverse-holder) and the question asks about
// A (chain SOURCE). The pool already encodes B-gender-keyed
// labels; the puzzle layer adds:
//   - aCharacter: a fresh figure for A, rendered as a non-yellow
//     avatar at the LEFT of the chain visual (before character[0]).
//     For ITHARA_BANDUVU (atoms empty), still rendered standalone
//     against the egoName(B) avatar with no atom arrows between.
//   - aGenderChosen: the gender variant picked for A in this
//     specific render (the inverse Telugu term may slash on A's
//     gender — e.g. [Father] → కొడుకు/కూతురు). UI uses this to
//     render the A avatar with the correct gender so the player
//     can disambiguate which side of the slash to pick.
//   - egoOnRight: always true for L7 — flags the renderer that
//     ego (B yellow) is at the END of the chain, not the start.
//   - symmetric / engineKey: passed through from pool L7Metadata.
export interface L7PuzzleMeta {
  tier: 1 | 2 | 3 | 4
  symmetric?: boolean
  engineKey?: string
  aCharacter: PuzzleCharacter
  aGenderChosen: CharacterGender
  egoOnRight: true
}

export interface QuizPuzzle {
  level: number
  egoName: string
  egoGender: CharacterGender
  chain: Chain
  characters: PuzzleCharacter[]
  correct: KinshipLabel & { chainKey: string }
  options: AnswerOption[]   // length 4, shuffled
  /** Optional L6 named-term visualisation. When present,
   *  ChainVisualization should render one arrow per entry instead
   *  of one arrow per atom. */
  visualSteps?: VisualStep[]
  /** #quiz-l7-ui — present only on L7 puzzles. Renderer reads
   *  this to flip ego placement and add the A figure. */
  l7?: L7PuzzleMeta
}

// #quiz-l7-ui — atom → target-gender lookup, mirrored from the
// backend pool generator (apps/backend/src/services/quizChainPools.ts
// ATOM_TARGET_GENDER). Used to derive B's gender from the LAST atom
// of an L7 chain — that's the perspective-holder ego per Kinship's
// locked contract.
export const ATOM_TARGET_GENDER: Record<QuizAtom, CharacterGender> = {
  Father: 'male',  Mother: 'female',
  Son: 'male',     Daughter: 'female',
  Brother: 'male', Sister: 'female',
  Husband: 'male', Wife: 'female',
}

// ── pool + chain picking ───────────────────────────────────────

/**
 * Pick a chain from the pool, deprioritizing those whose key is
 * in `recentChainKeys`. If every chain in the pool has been used
 * recently, all become eligible (avoids deadlock on small pools).
 */
export function pickChain(
  pool: readonly Chain[],
  recentChainKeys: ReadonlySet<string>,
  rng: () => number,
): Chain {
  if (pool.length === 0) throw new Error('pickChain: empty pool')
  const fresh = pool.filter((c) => !recentChainKeys.has(chainKey(c)))
  const candidates = fresh.length > 0 ? fresh : pool
  return candidates[Math.floor(rng() * candidates.length)]
}

/**
 * Pick ego's gender. Pre-login v1: random.
 */
export function pickEgoGender(rng: () => number): CharacterGender {
  return rng() < 0.5 ? 'male' : 'female'
}

/**
 * Build per-character records from a chain. Each character's
 * gender is forced by its atom; name is picked to be unique
 * within this puzzle, preferring names not in `recentNames`
 * (last-3-puzzles cross-puzzle exclusion).
 *
 * #kinship-quiz-pool-self-filter-and-names — Fix 3:
 * intra-puzzle uniqueness is enforced HARD, cross-puzzle
 * recency is enforced SOFT. The pre-fix path conflated the
 * two into a single excludeNames set; pickName's pool-exhaust
 * fallback could then return a name already used in the same
 * puzzle (5-slot L6 chain × 10-name gender pool was vulnerable
 * once cross-puzzle recents filled the gender pool).
 *
 * New logic: per slot, prefer a name not in EITHER set; if
 * cross-puzzle recents would exhaust the pool, relax recents
 * but never relax intra-puzzle uniqueness. With the 10-name
 * pools and at most 5 slots per puzzle, pickName's fallback
 * (which returns ANY pool name) cannot collide intra-puzzle
 * because intra-puzzle-used.size ≤ 4 < 10 at every call.
 */
export function assembleCharacters(
  chain: Chain,
  egoName: string,
  recentNames: ReadonlySet<string>,
  rng: () => number,
): PuzzleCharacter[] {
  const intraPuzzleUsed = new Set<string>([egoName])
  const characters: PuzzleCharacter[] = []
  for (const atom of chain) {
    const gender = atomForcedGender(atom)
    const name = pickNameTwoLayer(gender, intraPuzzleUsed, recentNames, rng)
    intraPuzzleUsed.add(name)
    characters.push({ name, gender, atom })
  }
  return characters
}

/**
 * Two-layer name picker: try names that satisfy BOTH the
 * intra-puzzle hard exclude and the cross-puzzle soft exclude.
 * If the ideal set is empty (recents have eaten the pool),
 * fall back to intra-puzzle-only — guaranteed to succeed when
 * pool size > intra-puzzle slot count.
 *
 * Wraps pickName so the existing single-set pickName API stays
 * intact (used by tests and the ego-name pick site).
 */
function pickNameTwoLayer(
  gender: CharacterGender,
  intraPuzzleUsed: ReadonlySet<string>,
  recentNames: ReadonlySet<string>,
  rng: () => number,
): string {
  const combined = new Set<string>([...intraPuzzleUsed, ...recentNames])
  // pickName excludes `combined` and falls back to full pool only
  // when ALL pool names are excluded. We re-implement that decision
  // here so the fallback respects intraPuzzleUsed.
  const pool = gender === 'male' ? MALE_NAMES_REF : FEMALE_NAMES_REF
  const ideal = pool.filter((n) => !combined.has(n))
  if (ideal.length > 0) {
    return ideal[Math.floor(rng() * ideal.length)]
  }
  // Relax cross-puzzle exclude; intraPuzzleUsed stays mandatory.
  return pickName(gender, intraPuzzleUsed, rng)
}

// ── lookup helpers ─────────────────────────────────────────────

function labelFor(
  lookup: EgoLookup,
  chain: Chain,
  egoGender: CharacterGender,
): KinshipLabel | undefined {
  return lookup.get(egoKey(egoGender, chain))
}

function flipEgo(g: CharacterGender): CharacterGender {
  return g === 'male' ? 'female' : 'male'
}

const FLIP: Record<QuizAtom, QuizAtom> = {
  Father: 'Mother', Mother: 'Father',
  Son: 'Daughter', Daughter: 'Son',
  Brother: 'Sister', Sister: 'Brother',
  // #kinship-quiz-wife-husband-atoms — spouse atoms flip as
  // gender-pairs; matches the server-side FLIP in
  // apps/backend/src/services/quizChainPools.ts.
  Wife: 'Husband', Husband: 'Wife',
}

function mutateAtomAt(chain: Chain, index: number): Chain {
  const out: Chain = [...chain]
  out[index] = FLIP[out[index]]
  return out
}

// ── distractor generators (per level) ──────────────────────────

interface DistractorCandidate {
  chain: Chain
  egoForLookup: CharacterGender
  term: string
  source: 'flipped-ego' | 'mutation' | 'pool' | 'adjacent-level'
}

// #kinship-quiz-distractor-synonyms — synonym groups for kinship
// terms that refer to the same kin slot. Distractors must not be
// in the same group as the correct answer, otherwise the player
// sees two "correct" answers on the options grid.
//
// Confirmed by Ramesh for gen-1 cross-niece/nephew:
//   మేనకోడలు (cross-niece) ≡ both brother-rooted and sister-rooted
//                            descriptive forms (అన్న కూతురు / అక్క కూతురు)
//   మేనల్లుడు (cross-nephew) ≡ analogous male variants
//
// The engine returns these as distinct keys/terms in different
// contexts (cross vs parallel descriptive), which is correct at
// the engine layer. At the quiz layer we treat them as synonyms
// for distractor purposes only.
//
// Each entry stores its .trim()-normalized form so the comparison
// tolerates either '/' or ' / ' whitespace variants — different
// code paths in the engine emit either form.
// #kinship-quiz-ego-aware-synonyms — synonym groups gain an `ego`
// axis so the Viyyankudu/Viyyapurālu/Todalludu/Thodikodalu cases
// classify correctly per ego. The flat structure from 33af6ab
// (which 1ad9999 inherited) treated all four sub-types of
// Viyyankudu (anna for female, bava for male) as a single group
// — that marked anna/tammudu as a correct answer for male-ego
// viyyankudu chains, implying parallel-cousin marriages which
// violate Dravidian kinship.
//
// 'any' groups stay ego-agnostic (descriptive niece/nephew forms
// fall in this bucket — they're the same kin slot regardless of
// ego).
interface EgoConditionalSynonymGroup {
  ego: 'male' | 'female' | 'any'
  terms: ReadonlySet<string>
}

const EGO_CONDITIONAL_SYNONYM_GROUPS: ReadonlyArray<EgoConditionalSynonymGroup> = [
  // Cross-niece / cross-nephew descriptive forms — same kin slot
  // for either ego.
  { ego: 'any', terms: new Set([
    'మేనకోడలు',
    'అన్న కూతురు/తమ్ముడి కూతురు',
    'అన్న కూతురు / తమ్ముడి కూతురు',
    'అక్క కూతురు/చెల్లి కూతురు',
    'అక్క కూతురు / చెల్లి కూతురు',
  ]) },
  { ego: 'any', terms: new Set([
    'మేనల్లుడు',
    'అన్న కొడుకు/తమ్ముడి కొడుకు',
    'అన్న కొడుకు / తమ్ముడి కొడుకు',
    'అక్క కొడుకు/చెల్లి కొడుకు',
    'అక్క కొడుకు / చెల్లి కొడుకు',
  ]) },

  // Viyyankudu (son's/daughter's father-in-law):
  //   male ego  → bava-class (కావం in-law via cross-cousin marriage)
  //   female ego → anna-class (brother-merger)
  // The cross-cousin marriage rationale: a male ego's child marries
  // a sibling-in-law's child, not a sibling's child. Treating anna
  // as correct for male-ego viyyankudu would imply brother's
  // children marrying — violates the kinship system.
  { ego: 'male',   terms: new Set([
    'వియ్యంకుడు', 'బావ/బావమరిది', 'బావ / బావమరిది',
  ]) },
  { ego: 'female', terms: new Set([
    'వియ్యంకుడు', 'అన్న/తమ్ముడు', 'అన్న / తమ్ముడు',
  ]) },

  // Viyyapurālu (son's/daughter's mother-in-law):
  //   male ego  → akka-class (sister-merger)
  //   female ego → vadina-class (sister-in-law via cross-cousin marriage)
  { ego: 'male',   terms: new Set([
    'వియ్యపురాలు', 'అక్క/చెల్లి', 'అక్క / చెల్లి',
  ]) },
  { ego: 'female', terms: new Set([
    'వియ్యపురాలు', 'వదిన/మరదలు', 'వదిన / మరదలు',
  ]) },

  // Todalludu (male-only) — ego's wife's sister's husband =
  // brother-class via marriage merger.
  { ego: 'male', terms: new Set([
    'తోడల్లుడు', 'అన్న/తమ్ముడు', 'అన్న / తమ్ముడు',
  ]) },
  // Thodikodalu (female-only) — ego's husband's brother's wife =
  // sister-class via marriage merger.
  { ego: 'female', terms: new Set([
    'తోడికోడలు', 'అక్క/చెల్లి', 'అక్క / చెల్లి',
  ]) },
]

// #kinship-quiz-name-gender-and-viyyankudu-synonyms — structural
// detector for Viyyankudu / Viyyapurālu chains. Returns the slot
// name if the chain matches the 3-atom [child, spouse, parent]
// shape, otherwise null.
//
// Used to gate the "show the generic term as a correct option"
// behavior — without this gate the synonym filter would always
// substitute the generic for the ego-specific term, including on
// unrelated chains where the answer just happens to be anna/bava.
export function detectViyyankuduSlot(chain: Chain): 'viyyankudu' | 'viyyapuralu' | null {
  if (chain.length !== 3) return null
  const [a, b, c] = chain
  const spousePair =
    (a === 'Son'      && b === 'Wife') ||
    (a === 'Daughter' && b === 'Husband')
  if (!spousePair) return null
  if (c === 'Father') return 'viyyankudu'
  if (c === 'Mother') return 'viyyapuralu'
  return null
}

const VIYYANKUDU_GENERIC_TE = 'వియ్యంకుడు'
const VIYYAPURALU_GENERIC_TE = 'వియ్యపురాలు'

// #kinship-quiz-name-gender-and-viyyankudu-synonyms — ego gender
// constraint derived from a chain's first atom. Wife as first atom
// requires the ego to be male (a Bharya only exists for a male
// ego); Husband requires female. Other first atoms leave ego
// unconstrained.
//
// Surfaced as a separate helper so RelationQuizV2's puzzle
// generator can override its random ego pick when the chain
// dictates one.
export function requiredEgoForChain(chain: Chain): CharacterGender | null {
  if (chain.length === 0) return null
  if (chain[0] === 'Wife') return 'male'
  if (chain[0] === 'Husband') return 'female'
  return null
}

/**
 * Returns true if `a` and `b` refer to the same kinship slot
 * per KINSHIP_SYNONYM_GROUPS. Trim-tolerant; identical strings
 * are trivially synonymous.
 */
export function termsAreSynonyms(
  a: string,
  b: string,
  ego: CharacterGender | 'any' = 'any',
): boolean {
  const aTrim = a.trim()
  const bTrim = b.trim()
  if (aTrim === bTrim) return true
  for (const group of EGO_CONDITIONAL_SYNONYM_GROUPS) {
    // 'any'-tagged groups always apply. ego-tagged groups apply
    // only when the caller's ego matches (or when the caller
    // passed 'any' to ask "synonymous under ANY ego" — kept for
    // legacy/tests).
    if (group.ego !== 'any' && ego !== 'any' && group.ego !== ego) continue
    if (group.terms.has(aTrim) && group.terms.has(bTrim)) return true
  }
  return false
}

/**
 * Seeds a `seen` Set with `term` PLUS every synonym of `term`
 * found in EGO_CONDITIONAL_SYNONYM_GROUPS for the given ego.
 * Mutates the set in place; idempotent. Each entry is stored
 * .trim()-normalized.
 */
function markSeenWithSynonyms(
  seen: Set<string>,
  term: string,
  ego: CharacterGender | 'any' = 'any',
): void {
  const trimmed = term.trim()
  seen.add(trimmed)
  for (const group of EGO_CONDITIONAL_SYNONYM_GROUPS) {
    if (group.ego !== 'any' && ego !== 'any' && group.ego !== ego) continue
    if (group.terms.has(trimmed)) {
      for (const syn of group.terms) seen.add(syn.trim())
    }
  }
}

/**
 * Pool fallback — picks from a chain pool, skipping the correct
 * chain + colliding terms + self-loops + synonyms of the correct
 * answer. Powers Tier 2 (within-level) and Tier 3 (adjacent-level)
 * distractor fallback.
 *
 * The `source` arg distinguishes the two: 'pool' for the level's
 * own chains, 'adjacent-level' for cross-level fallback via the
 * #kinship-quiz-distractor-fallback path. Telemetry consumes this
 * to track how often the fallback fires.
 */
function poolDistractors(
  correctChain: Chain,
  correctTerm: string,
  pool: readonly Chain[],
  lookup: EgoLookup,
  egoGender: CharacterGender,
  alreadySeenTerms: ReadonlySet<string>,
  source: 'pool' | 'adjacent-level' = 'pool',
): DistractorCandidate[] {
  const correctKey = chainKey(correctChain)
  const seen = new Set<string>()
  markSeenWithSynonyms(seen, correctTerm, egoGender)
  for (const t of alreadySeenTerms) markSeenWithSynonyms(seen, t, egoGender)
  const candidates: DistractorCandidate[] = []
  for (const c of pool) {
    if (chainKey(c) === correctKey) continue
    const label = labelFor(lookup, c, egoGender)
    if (!label || isSelfTerm(label.term)) continue
    if (seen.has(label.term.trim())) continue
    markSeenWithSynonyms(seen, label.term, egoGender)
    candidates.push({ chain: c, egoForLookup: egoGender, term: label.term, source })
  }
  return candidates
}

/**
 * L3 distractor strategy — three explicit mutations:
 *   • flipped ego (same chain, OTHER ego gender)
 *   • descent flipped (Son↔Daughter at index 1)
 *   • sibling flipped (Brother↔Sister at index 0)
 *
 * If any mutation collides with the correct term or with another
 * already-picked distractor, fall back to a pool pick for that
 * slot. Returns up to 3 distinct distractors.
 */
function l3Distractors(
  correctChain: Chain,
  correctTerm: string,
  pool: readonly Chain[],
  lookup: EgoLookup,
  egoGender: CharacterGender,
): DistractorCandidate[] {
  if (correctChain.length !== 2) {
    throw new Error(`l3Distractors: expected length-2 chain, got ${correctChain.length}`)
  }
  const seen = new Set<string>()
  markSeenWithSynonyms(seen, correctTerm, egoGender)
  const picks: DistractorCandidate[] = []

  const attempts: Array<{ chain: Chain; ego: CharacterGender; source: 'flipped-ego' | 'mutation' }> = [
    { chain: correctChain,             ego: flipEgo(egoGender), source: 'flipped-ego' },
    { chain: mutateAtomAt(correctChain, 1), ego: egoGender,          source: 'mutation' },
    { chain: mutateAtomAt(correctChain, 0), ego: egoGender,          source: 'mutation' },
  ]
  for (const a of attempts) {
    const label = labelFor(lookup, a.chain, a.ego)
    if (!label || isSelfTerm(label.term)) continue
    if (seen.has(label.term.trim())) continue
    markSeenWithSynonyms(seen, label.term, egoGender)
    picks.push({ chain: a.chain, egoForLookup: a.ego, term: label.term, source: a.source })
  }
  if (picks.length < 3) {
    const pool3 = poolDistractors(correctChain, correctTerm, pool, lookup, egoGender, seen)
    for (const p of pool3) {
      if (picks.length >= 3) break
      picks.push(p)
      markSeenWithSynonyms(seen, p.term, egoGender)
    }
  }
  return picks.slice(0, 3)
}

/**
 * L4 distractor strategy — mutate each atom in order. Falls back
 * to pool-other-chain on collision.
 */
function l4Distractors(
  correctChain: Chain,
  correctTerm: string,
  pool: readonly Chain[],
  lookup: EgoLookup,
  egoGender: CharacterGender,
): DistractorCandidate[] {
  if (correctChain.length !== 3) {
    throw new Error(`l4Distractors: expected length-3 chain, got ${correctChain.length}`)
  }
  const seen = new Set<string>()
  markSeenWithSynonyms(seen, correctTerm, egoGender)
  const picks: DistractorCandidate[] = []

  for (let i = 0; i < 3; i++) {
    const mutated = mutateAtomAt(correctChain, i)
    const label = labelFor(lookup, mutated, egoGender)
    if (!label || isSelfTerm(label.term)) continue
    if (seen.has(label.term.trim())) continue
    markSeenWithSynonyms(seen, label.term, egoGender)
    picks.push({ chain: mutated, egoForLookup: egoGender, term: label.term, source: 'mutation' })
  }
  if (picks.length < 3) {
    const pool3 = poolDistractors(correctChain, correctTerm, pool, lookup, egoGender, seen)
    for (const p of pool3) {
      if (picks.length >= 3) break
      picks.push(p)
      markSeenWithSynonyms(seen, p.term, egoGender)
    }
  }
  return picks.slice(0, 3)
}

/**
 * L5 distractor strategy — descent-then-ascent. Three explicit
 * mutations, identical in spirit to L3 but for length-3 chains
 * with the [desc, ascent, sib] shape:
 *   1. Same chain, FLIPPED ego — the prime pedagogical distractor.
 *      [Son, Mother, Brother] swings between male's బావమరిది and
 *      female's తమ్ముడు depending on ego; a player who misses the
 *      ego gender picks this option.
 *   2. Final atom flipped (Brother↔Sister at index 2)
 *   3. Descent atom flipped (Son↔Daughter at index 0)
 * Pool fallback for collisions.
 */
function l5Distractors(
  correctChain: Chain,
  correctTerm: string,
  pool: readonly Chain[],
  lookup: EgoLookup,
  egoGender: CharacterGender,
): DistractorCandidate[] {
  if (correctChain.length !== 3) {
    throw new Error(`l5Distractors: expected length-3 chain, got ${correctChain.length}`)
  }
  const seen = new Set<string>()
  markSeenWithSynonyms(seen, correctTerm, egoGender)
  const picks: DistractorCandidate[] = []
  const attempts: Array<{ chain: Chain; ego: CharacterGender; source: 'flipped-ego' | 'mutation' }> = [
    { chain: correctChain,             ego: flipEgo(egoGender), source: 'flipped-ego' },
    { chain: mutateAtomAt(correctChain, 2), ego: egoGender,     source: 'mutation' },
    { chain: mutateAtomAt(correctChain, 0), ego: egoGender,     source: 'mutation' },
  ]
  for (const a of attempts) {
    const label = labelFor(lookup, a.chain, a.ego)
    if (!label || isSelfTerm(label.term)) continue
    if (seen.has(label.term.trim())) continue
    markSeenWithSynonyms(seen, label.term, egoGender)
    picks.push({ chain: a.chain, egoForLookup: a.ego, term: label.term, source: a.source })
  }
  if (picks.length < 3) {
    const pool3 = poolDistractors(correctChain, correctTerm, pool, lookup, egoGender, seen)
    for (const p of pool3) {
      if (picks.length >= 3) break
      picks.push(p)
      markSeenWithSynonyms(seen, p.term, egoGender)
    }
  }
  return picks.slice(0, 3)
}

/**
 * L6 distractor strategy. Post #kinship-quiz-level-6-named-terms,
 * L6 chains vary in length (2-4 atoms depending on the named term
 * + direct atom combination). Strategy:
 *
 *   Tier 1 — single-atom mutations of the expanded chain (each
 *   position flipped via the FLIP map). Synonym-filtered.
 *   Tier 2 — L6 pool fallback (other entries in the L6 pool).
 *   Tier 3 — adjacent L5 pool fallback (handled by pickDistractors
 *            outer wrapper via adjacentPools arg).
 *
 * The dispatch's richer "mutation-named-term" vs "mutation-direct-
 * atom" source tagging would require threading the L6 metadata
 * (namedKey, directAtom) through the distractor pipeline. Left as
 * a follow-up — the current 'mutation' / 'pool' / 'adjacent-level'
 * tags suffice for gameplay; telemetry granularity can be added
 * if/when needed.
 */
function l6Distractors(
  correctChain: Chain,
  correctTerm: string,
  pool: readonly Chain[],
  lookup: EgoLookup,
  egoGender: CharacterGender,
): DistractorCandidate[] {
  if (correctChain.length < 2) {
    throw new Error(`l6Distractors: chain too short (${correctChain.length}); expected ≥ 2`)
  }
  const seen = new Set<string>()
  markSeenWithSynonyms(seen, correctTerm, egoGender)
  const picks: DistractorCandidate[] = []
  for (let i = 0; i < correctChain.length; i++) {
    if (picks.length >= 3) break
    const mutated = mutateAtomAt(correctChain, i)
    const label = labelFor(lookup, mutated, egoGender)
    if (!label || isSelfTerm(label.term)) continue
    if (seen.has(label.term.trim())) continue
    markSeenWithSynonyms(seen, label.term, egoGender)
    picks.push({ chain: mutated, egoForLookup: egoGender, term: label.term, source: 'mutation' })
  }
  if (picks.length < 3) {
    const pool3 = poolDistractors(correctChain, correctTerm, pool, lookup, egoGender, seen)
    for (const p of pool3) {
      if (picks.length >= 3) break
      picks.push(p)
      markSeenWithSynonyms(seen, p.term, egoGender)
    }
  }
  return picks.slice(0, 3)
}

/**
 * Generic pool-based distractors for L1 + L2.
 */
function poolOnlyDistractors(
  correctChain: Chain,
  correctTerm: string,
  pool: readonly Chain[],
  lookup: EgoLookup,
  egoGender: CharacterGender,
): DistractorCandidate[] {
  // #kinship-quiz-distractor-synonyms — same relaxation as the
  // outer pickDistractors throw: return whatever the pool yields,
  // pickDistractors decides whether to throw on zero or just warn.
  return poolDistractors(correctChain, correctTerm, pool, lookup, egoGender, new Set())
}

export function pickDistractors(
  level: number,
  correctChain: Chain,
  correctTerm: string,
  pool: readonly Chain[],
  lookup: EgoLookup,
  egoGender: CharacterGender,
  rng: () => number,
  // #kinship-quiz-distractor-fallback — Tier 3 cross-level
  // fallback. Caller (RelationQuizV2) supplies the chain lists
  // for adjacent levels in priority order: typically [L_{N-1},
  // L_{N+1}], with skip rules (no L0; no L6 per
  // #kinship-quiz-defer-l6). pickDistractors consults these only
  // when Tier 1 + Tier 2 yield < 3.
  adjacentPools: ReadonlyArray<readonly Chain[]> = [],
): AnswerOption[] {
  let candidates: DistractorCandidate[]
  switch (level) {
    case 1: case 2:
      candidates = poolOnlyDistractors(correctChain, correctTerm, pool, lookup, egoGender); break
    case 3:
      candidates = l3Distractors(correctChain, correctTerm, pool, lookup, egoGender); break
    case 4:
      candidates = l4Distractors(correctChain, correctTerm, pool, lookup, egoGender); break
    case 5:
      candidates = l5Distractors(correctChain, correctTerm, pool, lookup, egoGender); break
    case 6:
      candidates = l6Distractors(correctChain, correctTerm, pool, lookup, egoGender); break
    default:
      candidates = poolOnlyDistractors(correctChain, correctTerm, pool, lookup, egoGender); break
  }
  // #kinship-quiz-distractor-fallback — Tier 3: pull from adjacent
  // levels' chain pools when Tier 1 + Tier 2 yielded < 3. The
  // synonym filter is preserved (poolDistractors applies it via
  // markSeenWithSynonyms). De-dup against already-picked terms is
  // also preserved.
  if (candidates.length < 3 && adjacentPools.length > 0) {
    const seenTerms = new Set<string>([correctTerm, ...candidates.map((c) => c.term)])
    for (const adjPool of adjacentPools) {
      if (candidates.length >= 3) break
      const adjCands = poolDistractors(
        correctChain, correctTerm, adjPool, lookup, egoGender, seenTerms, 'adjacent-level',
      )
      for (const c of adjCands) {
        if (candidates.length >= 3) break
        candidates.push(c)
        seenTerms.add(c.term)
      }
    }
  }
  // #kinship-quiz-distractor-synonyms — 0 distractors stays the
  // hard floor (impossible-puzzle); warn only when below 3 even
  // after Tier 3. UI renders options.length cells. With current
  // pool sizes, Tier 3 should make < 3 effectively unreachable
  // (the warn fires only on a real defect).
  if (candidates.length === 0) {
    throw new Error(
      `pickDistractors level ${level}: zero distractors for chain ` +
      `[${correctChain.join(',')}] — pool may be corrupt or fully synonymous with correct answer`,
    )
  }
  if (candidates.length < 3) {
    console.warn(
      `pickDistractors level ${level}: only ${candidates.length} distractor(s) ` +
      `for chain [${correctChain.join(',')}] even after adjacent-level fallback; ` +
      `UI will show ${candidates.length + 1} options instead of 4`,
    )
  }
  const shuffled = shuffleInPlace([...candidates], rng).slice(0, 3)
  return shuffled.map((d) => ({
    chainKey: `${d.egoForLookup}|${chainKey(d.chain)}`,
    term: d.term,
    isCorrect: false,
    source: d.source,
  }))
}

/**
 * L6 pool curation — at chain-pool init time, take all 1296 candidate
 * chains, drop "self" results (per either ego), dedupe by the
 * (male-term, female-term) pair, random-sample maxSize chains.
 * Returns the curated pool ready for puzzle generation.
 */
export function filterAndSampleL6Pool(
  candidates: readonly Chain[],
  lookup: EgoLookup,
  maxSize: number,
  rng: () => number,
): Chain[] {
  const seen = new Set<string>()
  const filtered: Chain[] = []
  for (const c of candidates) {
    const male   = lookup.get(`male|${chainKey(c)}`)
    const female = lookup.get(`female|${chainKey(c)}`)
    if (!male || !female) continue
    if (isSelfTerm(male.term) || isSelfTerm(female.term)) continue
    const dedupKey = `${male.term}||${female.term}`
    if (seen.has(dedupKey)) continue
    seen.add(dedupKey)
    filtered.push(c)
  }
  return shuffleInPlace([...filtered], rng).slice(0, maxSize)
}

// ── shuffle ─────────────────────────────────────────────────────

export function shuffleInPlace<T>(arr: T[], rng: () => number): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
  return arr
}

// ── recent-puzzle name window ──────────────────────────────────
// #kinship-quiz-pickname-exhausted
//
// Cross-puzzle name uniqueness used to be tracked as a flat
// Set<string> with a name-count cap (~20). That cap exceeded each
// gender pool's size (10), so a streak of same-gender chains would
// exhaust the pool and throw out of pickName. Now we track a
// LIST of per-puzzle name sets and cap by PUZZLE COUNT. With
// RECENT_PUZZLE_WINDOW = 3 and ≤5 characters per puzzle, the
// excluded-name union is at most ~15 names — still less than the
// 20 total names across both gender pools, so per-gender excludes
// peak well below 10. pickName's fallback is the second line of
// defense if the window is misconfigured.

export const RECENT_PUZZLE_WINDOW = 3

// PuzzleHistory is a readonly array of per-puzzle name sets. Sets
// themselves are mutable but never mutated by these helpers — both
// return fresh allocations.
export type PuzzleHistory = ReadonlyArray<Set<string>>

/** Names used in the most-recent N puzzles, flattened to a single set. */
export function unionRecentNames(history: PuzzleHistory): Set<string> {
  const out = new Set<string>()
  for (const names of history) {
    for (const n of names) out.add(n)
  }
  return out
}

/**
 * Append the just-generated puzzle's name set to the history and
 * trim to the last `window` entries. Returns a NEW array; does not
 * mutate the input.
 */
export function pushPuzzleNames(
  history: PuzzleHistory,
  newNames: Iterable<string>,
  window: number = RECENT_PUZZLE_WINDOW,
): Set<string>[] {
  const appended: Set<string>[] = [...history, new Set(newNames)]
  return appended.slice(-window)
}

// ── orchestrator ─────────────────────────────────────────────────

/**
 * #kinship-quiz-chainpool-server — pick a pool entry, deprioritizing
 * those whose chainKey is in `recentChainKeys`. Adapter for the new
 * server-side pool path; mirrors pickChain's semantics for the
 * pre-server callers but operates on PoolEntry directly.
 */
export function pickEntry(
  pool: readonly PoolEntry[],
  recentChainKeys: ReadonlySet<string>,
  rng: () => number,
): PoolEntry {
  if (pool.length === 0) throw new Error('pickEntry: empty pool')
  const fresh = pool.filter((e) => !recentChainKeys.has(chainKey(e.atoms)))
  const candidates = fresh.length > 0 ? fresh : pool
  return candidates[Math.floor(rng() * candidates.length)]
}

/**
 * #kinship-quiz-chainpool-server — new entry point that consumes
 * server-precomputed PoolEntry[]. Internally constructs the
 * (Chain[], EgoLookup) shape that the legacy buildPuzzle expects
 * — the legacy path is preserved so the existing distractor
 * strategies, validator, and tests stay intact. Self-loop
 * filtering + L6 sampling are now done server-side at pool
 * generation time, so the client receives an already-curated
 * pool with precomputed mutation labels per entry.
 *
 * A future cleanup dispatch can collapse the legacy code path
 * once the server-side endpoint has been the only production
 * path for long enough to retire the legacy tests.
 */
// #kinship-quiz-pool-self-filter-and-names — Fix 2.
//
// Defensive: the backend pool filter (commit afa955f) should
// guarantee no entry resolves to self for the ego we'll use, but
// if a future engine change or pool-gen regression sneaks a bad
// entry through, we'd rather throw + retry than render a puzzle
// whose "correct answer" is the self string + whose marked-correct
// option ends up being a mutation distractor.
//
// Sources of truth for "what is self":
//   - 'సొంత వ్యక్తి' (telugu.json specialTerms.self)
//   - 'Self' (defensive — if the pool ever wires English mode)
//   - empty / whitespace-only term (shouldn't happen)
const SELF_TE_NORM = 'సొంత వ్యక్తి'.normalize()
const SELF_EN_NORM = 'Self'.normalize()

function isSelfLabel(term: string | undefined): boolean {
  if (!term) return true
  const norm = term.trim().normalize()
  return norm === SELF_TE_NORM || norm === SELF_EN_NORM
}

export function buildPuzzleFromPoolEntry(args: {
  level:         number
  egoGender:     CharacterGender
  entry:         PoolEntry
  pool:          readonly PoolEntry[]
  // #kinship-quiz-distractor-fallback — adjacent levels' pools in
  // priority order (typically [L_{N-1}, L_{N+1}]). Used as Tier 3
  // distractor fallback when within-level Tier 1+2 yields < 3 (the
  // synonym filter can shrink L3's effective pool below 3).
  adjacentPools?: ReadonlyArray<readonly PoolEntry[]>
  recentNames:   ReadonlySet<string>
  rng:           () => number
}): QuizPuzzle {
  // #kinship-quiz-level-6-named-terms — L6 entries carry per-ego
  // atom expansions in entry.l6.atomsByEgo. For L6 puzzles, the
  // actual chain to walk is the ego-specific expansion (Bava and
  // Vadina differ between male and female ego); for L1-L5 the
  // entry.atoms field is the chain.
  const effectiveChain: QuizAtom[] = args.entry.l6
    ? args.entry.l6.atomsByEgo[args.egoGender]
    : args.entry.atoms

  // Defensive throw — the pool filter should have caught self
  // entries before they reach the client. If it didn't (engine
  // drift, pool-gen regression, etc.), we'd rather fail loudly
  // so the component-level retry picks a different entry.
  const correctLabel = args.entry.labels[args.egoGender]
  if (isSelfLabel(correctLabel?.term)) {
    throw new Error(
      `buildPuzzleFromPoolEntry: pool entry resolves to self for ` +
      `ego=${args.egoGender}; chain=[${effectiveChain.join(',')}], ` +
      `term="${correctLabel?.term}". Backend pool filter should have ` +
      `caught this — see #kinship-quiz-pool-self-filter.`,
    )
  }

  const lookup: Map<EgoLookupKey, KinshipLabel> = new Map()
  const chainPool: Chain[] = []
  const adjacentChainPools: Chain[][] = []
  // Within-level entries: their per-ego atoms become the chainPool;
  // mutation labels add to the EgoLookup so the within-level
  // mutation distractor strategy works. For L6, use atomsByEgo for
  // each entry to keep the lookup consistent with the ego the
  // puzzle is being built for.
  for (const e of args.pool) {
    const eChain = e.l6 ? e.l6.atomsByEgo[args.egoGender] : e.atoms
    chainPool.push(eChain)
    // Seed both ego sides into the lookup so cross-ego distractor
    // probing (existing logic) still works for L1-L5; for L6 we
    // index both ego expansions of each entry.
    if (e.l6) {
      lookup.set(egoKey('male',   e.l6.atomsByEgo.male),   e.labels.male)
      lookup.set(egoKey('female', e.l6.atomsByEgo.female), e.labels.female)
    } else {
      lookup.set(egoKey('male',   e.atoms), e.labels.male)
      lookup.set(egoKey('female', e.atoms), e.labels.female)
    }
    for (const m of e.mutations) {
      lookup.set(egoKey('male',   m.atoms), m.labels.male)
      lookup.set(egoKey('female', m.atoms), m.labels.female)
    }
  }
  // Adjacent-level entries: chains kept separate (passed to Tier 3),
  // labels merged into the same EgoLookup so poolDistractors can
  // look up their terms without knowing about levels.
  for (const adjPool of args.adjacentPools ?? []) {
    const adjChains: Chain[] = []
    for (const e of adjPool) {
      const eChain = e.l6 ? e.l6.atomsByEgo[args.egoGender] : e.atoms
      adjChains.push(eChain)
      lookup.set(egoKey('male',   eChain), e.labels.male)
      lookup.set(egoKey('female', eChain), e.labels.female)
      for (const m of e.mutations) {
        lookup.set(egoKey('male',   m.atoms), m.labels.male)
        lookup.set(egoKey('female', m.atoms), m.labels.female)
      }
    }
    adjacentChainPools.push(adjChains)
  }
  const puzzle = buildPuzzle({
    level:         args.level,
    egoGender:     args.egoGender,
    chain:         effectiveChain,
    pool:          chainPool,
    adjacentPools: adjacentChainPools,
    lookup,
    recentNames:   args.recentNames,
    rng:           args.rng,
  })

  // #kinship-quiz-level-6-named-terms — attach visualSteps for L6
  // so the chain visualisation renders 2 arrows (named term +
  // direct atom) instead of N arrows (one per atom). The named-
  // term arrow lands on the last character of the expansion; the
  // direct-atom arrow lands on the final character.
  if (args.entry.l6) {
    const expansionLength = effectiveChain.length - 1   // direct atom is last
    const namedTermDisplay = NAMED_TERM_DISPLAY_TE[args.entry.l6.namedKey]
      ?? args.entry.l6.namedKey
    puzzle.visualSteps = [
      { label: namedTermDisplay,        characterIndex: expansionLength - 1 },
      { label: directAtomLabelTe(args.entry.l6.directAtom),
        characterIndex: effectiveChain.length - 1 },
    ]
  }

  return puzzle
}

// #quiz-l7-ui — L7 puzzle builder. L7 entries flip the perspective:
// ego is B (chain target), the labels are keyed on B's gender (set
// by the chain's last atom per ATOM_TARGET_GENDER), and the player
// picks what A is to B (the INVERSE Telugu term — pool already
// computed this).
//
// Builds a QuizPuzzle with:
//   - egoName/egoGender = B's name + gender
//   - chain = forward atoms (engine direction, unchanged)
//   - characters = forward-destination characters; characters[N-1]
//     IS B (= ego, rendered yellow at the END of the visual)
//   - l7.aCharacter = fresh A character, rendered as the first
//     non-yellow avatar at the LEFT of the visual
//   - correct.term = labels[bGender].term (the inverse answer)
//   - options = correct + 3 distractors from other L7 entries
//
// For ITHARA_BANDUVU (atoms empty), characters is empty and the
// visual is just A → B with no atom arrows; labels.male === female.
export function buildPuzzleFromPoolEntryL7(args: {
  entry:        PoolEntry
  pool:         readonly PoolEntry[]
  recentNames:  ReadonlySet<string>
  rng:          () => number
}): QuizPuzzle {
  const { entry, pool, recentNames, rng } = args

  // B's gender: locked by the chain's last atom (per Kinship's
  // contract). For atom-less ITHARA_BANDUVU, both labels match —
  // we pick B's gender randomly so the avatar isn't always male.
  const atoms = entry.atoms
  const bGender: CharacterGender = atoms.length > 0
    ? ATOM_TARGET_GENDER[atoms[atoms.length - 1]]
    : pickEgoGender(rng)

  // A's gender: names imply gender (Anitha = female, Manoj = male).
  // For most chains A is unconstrained — pick randomly. For chains
  // starting with Wife/Husband atom, the source gender IS locked
  // (only women have husbands, only men have wives), so override
  // the random pick with the atom-required gender. Without this
  // override, [Husband] L7 chains would pick a male A (Manoj)
  // walking the Husband atom — incoherent (only women have
  // husbands).
  // #quiz-l7-perspective-inversion Bug-3 — reuses requiredEgoForChain
  // (L1-L6 helper) since A's gender constraint comes from the SAME
  // first-atom rule — for L7, A IS the engine-side ego (the source
  // of the forward chain walk).
  const aGender: CharacterGender = requiredEgoForChain(atoms) ?? pickEgoGender(rng)

  // #kinship-l7-name-gender-resolution — prefer the 4-slot resolved
  // label when the pool provides it (post-fix deploys). Fall back to
  // the legacy slash-joined labels.male/female when l7Labels is
  // absent (older bundle in cache, or a transient missing-field
  // case). The fallback term may still contain slash forms — the UI
  // renders what it gets.
  const slotKey = `${bGender}B_${aGender}A` as keyof NonNullable<typeof entry.l7Labels>
  const correctLabel: KinshipLabel = entry.l7Labels?.[slotKey] ?? entry.labels[bGender]
  if (isSelfLabel(correctLabel?.term)) {
    throw new Error(
      `buildPuzzleFromPoolEntryL7: pool entry resolves to self for ` +
      `bGender=${bGender}, aGender=${aGender}; ` +
      `engineKey=${entry.l7?.engineKey ?? '(atom-based)'}, ` +
      `term="${correctLabel?.term}".`,
    )
  }

  // Names. egoName (B) is one of the bGender pool, aCharacter is
  // aGender. Use a working name-exclusion set to keep all the
  // names in this puzzle unique.
  const usedNames = new Set<string>(recentNames)
  const bName = pickName(bGender, usedNames, rng); usedNames.add(bName)
  const aName = pickName(aGender, usedNames, rng); usedNames.add(aName)

  // Intermediate characters: atoms 0..N-2 produce intermediates;
  // atoms[N-1] produces B (which IS the ego). For atoms.length === 0
  // (ITHARA_BANDUVU), characters is empty — visual = A → B directly.
  const characters: PuzzleCharacter[] = []
  for (let i = 0; i < atoms.length; i++) {
    const atomGender = ATOM_TARGET_GENDER[atoms[i]]
    // Last character IS B (the ego). Use bName so the visual avatar
    // at the END of the chain matches the ego rendered as yellow.
    const charName = i === atoms.length - 1 ? bName : pickName(atomGender, usedNames, rng)
    if (i !== atoms.length - 1) usedNames.add(charName)
    characters.push({ atom: atoms[i], gender: atomGender, name: charName })
  }

  const aCharacter: PuzzleCharacter = {
    atom: 'Brother', // arbitrary placeholder — A has no incoming atom in the L7 visual; we just need a renderable shape
    gender: aGender,
    name: aName,
  }

  // Distractors — pool-other-chain strategy. Pick 3 OTHER L7 entries'
  // labels (matching bGender side; for symmetric entries either label
  // works since they're equal). Filter out the correct term itself.
  const distractors: AnswerOption[] = []
  const seenTerms = new Set<string>([correctLabel.term])
  // Stable shuffle of the other-entries list.
  const others = pool.filter((e) => e !== entry).slice()
  for (let i = others.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [others[i], others[j]] = [others[j], others[i]]
  }
  // #kinship-l7-name-gender-resolution — distractors also flow
  // through the 4-slot resolution. Each other-entry contributes its
  // (otherBGender, aGender) slot — A's gender is fixed for THIS
  // puzzle so distractor terms shown to the player match the
  // visible A figure's gender. Fall back to the legacy slash-joined
  // labels[otherBGender] when l7Labels is absent.
  for (const other of others) {
    if (distractors.length >= 3) break
    const otherBGender: CharacterGender = other.atoms.length > 0
      ? ATOM_TARGET_GENDER[other.atoms[other.atoms.length - 1]]
      : 'male'
    const otherSlotKey = `${otherBGender}B_${aGender}A` as keyof NonNullable<typeof other.l7Labels>
    const otherLabel = other.l7Labels?.[otherSlotKey] ?? other.labels[otherBGender]
    const term = otherLabel?.term
    if (!term || seenTerms.has(term) || isSelfLabel(term)) continue
    seenTerms.add(term)
    distractors.push({
      chainKey: `l7|${other.atoms.join('|') || other.l7?.engineKey || '?'}|${otherBGender}|${aGender}`,
      term,
      isCorrect: false,
      source: 'pool',
    })
  }

  const correctOpt: AnswerOption = {
    chainKey: `l7|${atoms.join('|') || entry.l7?.engineKey || '?'}|${bGender}`,
    term: correctLabel.term,
    isCorrect: true,
    source: 'correct',
  }
  const options = shuffleInPlace([correctOpt, ...distractors], rng)

  return {
    level: 7,
    egoName: bName,
    egoGender: bGender,
    chain: atoms,
    characters,
    correct: { ...correctLabel, chainKey: correctOpt.chainKey },
    options,
    l7: {
      tier: entry.l7?.tier ?? 1,
      symmetric: entry.l7?.symmetric,
      engineKey: entry.l7?.engineKey,
      aCharacter,
      aGenderChosen: aGender,
      egoOnRight: true,
    },
  }
}

// #kinship-quiz-level-6-named-terms — display labels for the named
// terms used in L6 visualisation. Mirrors NAMED_TERMS.display.te
// from packages/relation-config/named-terms.ts; duplicated here so
// the web bundle doesn't have to import the engine package at
// runtime (the engine is server-side per #kinship-telugu-refine).
const NAMED_TERM_DISPLAY_TE: Record<string, string> = {
  mama:        'మామయ్య',
  atta:        'అత్తయ్య',
  menalludu:   'మేనల్లుడు',
  menakodalu:  'మేనకోడలు',
  bava:        'బావ',
  vadina:      'వదిన',
  bharya:      'భార్య',
  bharta:      'భర్త',
  kodalu:      'కోడలు',
  alludu:      'అల్లుడు',
  todalludu:   'తోడల్లుడు',
  thodikodalu: 'తోడికోడలు',
}

// Direct-atom label for the trailing arrow. Mirrors the atom
// Telugu labels in RelationQuizV2.ATOM_TELUGU but kept local
// so quizPuzzle.ts has no React dependency.
const DIRECT_ATOM_TE: Record<string, string> = {
  Father: 'తండ్రి', Mother: 'తల్లి',
  Son: 'కొడుకు',   Daughter: 'కూతురు',
  Brother: 'సోదరుడు', Sister: 'సోదరి',
  Wife: 'భార్య',    Husband: 'భర్త',
}

function directAtomLabelTe(atom: string): string {
  return DIRECT_ATOM_TE[atom] ?? atom
}

export function buildPuzzle(args: {
  level:         number
  egoGender:     CharacterGender
  chain:         Chain
  pool:          readonly Chain[]
  // #kinship-quiz-distractor-fallback — Tier 3 adjacent-level chains
  adjacentPools?: ReadonlyArray<readonly Chain[]>
  lookup:        EgoLookup
  recentNames:   ReadonlySet<string>
  rng:           () => number
}): QuizPuzzle {
  const { level, egoGender, chain, pool, lookup, recentNames, rng } = args
  const adjacentPools = args.adjacentPools ?? []

  const correctLabel = labelFor(lookup, chain, egoGender)
  if (!correctLabel) {
    throw new Error(
      `buildPuzzle: no label for chain [${chain.join(',')}] ego=${egoGender}`,
    )
  }

  const egoName    = pickName(egoGender, recentNames, rng)
  const characters = assembleCharacters(chain, egoName, recentNames, rng)

  const distractors = pickDistractors(level, chain, correctLabel.term, pool, lookup, egoGender, rng, adjacentPools)
  const correctOpt: AnswerOption = {
    chainKey: egoKey(egoGender, chain),
    term:     correctLabel.term,
    isCorrect: true,
    source: 'correct',
  }

  // #kinship-quiz-name-gender-and-viyyankudu-synonyms — Fix 3
  //
  // For chains whose structure encodes a Viyyankudu/Viyyapurālu
  // slot, surface the GENERIC Telugu term (వియ్యంకుడు /
  // వియ్యపురాలు) as an additional correct option alongside the
  // ego-specific sub-type (anna/bava/akka/vadina) the engine
  // returned. Player can pick either; both count as correct via
  // the synonym groups.
  //
  // Implementation: replace ONE distractor's term with the
  // generic-term + flip isCorrect=true. Keeps the 4-option grid
  // intact. Skipped if the synonym filter happened to surface
  // the generic already (defensive — markSeenWithSynonyms already
  // filters duplicates).
  const viyyankuduSlot = detectViyyankuduSlot(chain)
  const adjustedDistractors: AnswerOption[] = distractors.map((d) => ({ ...d }))
  if (viyyankuduSlot !== null && adjustedDistractors.length > 0) {
    const genericTerm = viyyankuduSlot === 'viyyankudu' ? VIYYANKUDU_GENERIC_TE : VIYYAPURALU_GENERIC_TE
    const alreadyPresent = adjustedDistractors.some((d) => d.term === genericTerm)
                        || correctOpt.term === genericTerm
    if (!alreadyPresent) {
      adjustedDistractors[0] = {
        chainKey: `viyyankudu-generic|${chainKey(chain)}`,
        term: genericTerm,
        isCorrect: true,
        source: 'correct',
      }
    }
  }

  const options = shuffleInPlace([correctOpt, ...adjustedDistractors], rng)

  const puzzle: QuizPuzzle = {
    level,
    egoName,
    egoGender,
    chain,
    characters,
    correct: { ...correctLabel, chainKey: egoKey(egoGender, chain) },
    options,
  }

  validateNameGenderConsistency({
    egoName, egoGender, chain, characters,
  })

  return puzzle
}
